import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Globe, RefreshCw, Plus, Trash2, Wallet, CircleDollarSign, Hourglass, TrendingUp, Search, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { gcApi, edificiosApi } from '../../lib/api';
import { formatDate } from '../../lib/utils';

const fmt = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);

const ESTADO_TONE = {
  pendiente: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200',
  parcial:   'bg-blue-100 text-blue-700 ring-1 ring-blue-200',
  cobrado:   'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200',
  vencido:   'bg-red-100 text-red-700 ring-1 ring-red-200',
  anulado:   'bg-slate-100 text-slate-500',
};

const ORIGEN_LABEL = {
  fee_admin_te: 'Fee admin TE',
  fee_admin_foresta: 'Fee admin Foresta',
  fee_busqueda: 'Fee búsqueda',
  honorario: 'Honorario',
  gasto_recuperable: 'Gasto recuperable',
  manual: 'Manual',
  otro: 'Otro',
};

const monthLabel = (per) => {
  const [y, m] = per.split('-');
  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${meses[Number(m) - 1]} ${y}`;
};

const currentPeriodo = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const shiftPeriodo = (per, delta) => {
  const [y, m] = per.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const AnalistaGlobalCitizen = () => {
  const [tab, setTab] = useState('resumen');
  const [periodo, setPeriodo] = useState(currentPeriodo());
  const [edificios, setEdificios] = useState([]);

  useEffect(() => {
    edificiosApi.list().then((r) => setEdificios(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="p-6 max-w-[1500px] mx-auto" data-testid="gc-module">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 ring-1 ring-emerald-200 flex items-center justify-center">
            <Globe className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Global Citizen</h1>
            <p className="text-xs text-slate-500">Tablero económico — Cuentas por cobrar, Cobros y Resultado mensual.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2 py-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setPeriodo(shiftPeriodo(periodo, -1))} data-testid="gc-period-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-semibold text-slate-700 px-2" data-testid="gc-current-period">{monthLabel(periodo)}</span>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setPeriodo(shiftPeriodo(periodo, 1))} data-testid="gc-period-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="resumen" data-testid="gc-tab-resumen">Resumen</TabsTrigger>
          <TabsTrigger value="cxc" data-testid="gc-tab-cxc">Cuentas por cobrar</TabsTrigger>
          <TabsTrigger value="cobros" data-testid="gc-tab-cobros">Cobros</TabsTrigger>
          <TabsTrigger value="egresos" data-testid="gc-tab-egresos">Egresos GC</TabsTrigger>
        </TabsList>

        <TabsContent value="resumen" className="mt-4">
          <ResumenView periodo={periodo} active={tab === 'resumen'} onNavigate={setTab} />
        </TabsContent>
        <TabsContent value="cxc" className="mt-4">
          <CxCView periodo={periodo} edificios={edificios} active={tab === 'cxc'} />
        </TabsContent>
        <TabsContent value="cobros" className="mt-4">
          <CobrosView periodo={periodo} edificios={edificios} active={tab === 'cobros'} />
        </TabsContent>
        <TabsContent value="egresos" className="mt-4">
          <EgresosView periodo={periodo} edificios={edificios} active={tab === 'egresos'} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============================= RESUMEN =============================
const KPICard = ({ icon: Icon, label, value, tone = 'slate', testId }) => (
  <div className={`bg-white border border-slate-200 rounded-lg p-4`} data-testid={testId}>
    <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className={`mt-1 text-2xl font-bold text-${tone}-900`}>{fmt(value)}</div>
  </div>
);

const ResumenView = ({ periodo, active, onNavigate }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await gcApi.resumen(periodo);
      setData(r.data);
    } catch (e) {
      toast.error('Error al cargar resumen GC');
    } finally { setLoading(false); }
  }, [periodo]);

  useEffect(() => { if (active) load(); }, [active, load]);

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const [rF, rTE, rC] = await Promise.allSettled([
        gcApi.syncFeesForesta(periodo),
        gcApi.syncFeesTE(periodo),
        gcApi.syncComisiones(),
      ]);
      const get = (r, k, d = 0) => r.status === 'fulfilled' ? (r.value.data?.[k] ?? d) : 0;
      const c = get(rF, 'cxc_creadas') + get(rTE, 'cxc_creadas') + get(rC, 'cxc_creadas');
      const u = get(rF, 'cxc_actualizadas') + get(rTE, 'cxc_actualizadas') + get(rC, 'cxc_actualizadas');
      const co = get(rTE, 'cobros_compensacion') + get(rC, 'cobros_mirror_creados');
      toast.success(`Sync ingresos GC: ${c} nuevas, ${u} actualizadas, ${co} cobros auto.`);
      await load();
    } catch (e) {
      toast.error('Error en sincronización');
    } finally { setSyncing(false); }
  };

  if (loading || !data) {
    return <div className="text-center py-12 text-slate-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  }
  const k = data.kpis;
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={handleSyncAll} disabled={syncing} className="h-9 bg-emerald-600 hover:bg-emerald-700" data-testid="gc-resumen-sync">
          {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
          Sincronizar ingresos GC
        </Button>
        <Button variant="outline" onClick={() => onNavigate?.('cxc')} className="h-9" data-testid="gc-resumen-go-cxc">Ver cuentas por cobrar</Button>
        <Button variant="outline" onClick={() => onNavigate?.('egresos')} className="h-9" data-testid="gc-resumen-go-egresos">Ver egresos GC</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard icon={TrendingUp} label="Devengado GC" value={k.devengado} tone="slate" testId="kpi-devengado" />
        <KPICard icon={Wallet} label="Cobrado GC" value={k.cobrado} tone="emerald" testId="kpi-cobrado" />
        <KPICard icon={Hourglass} label="Pendiente" value={k.pendiente} tone="amber" testId="kpi-pendiente" />
        <KPICard icon={CircleDollarSign} label="Egresos GC" value={k.egresos_gc} tone="red" testId="kpi-egresos" />
        <KPICard icon={TrendingUp} label="Resultado caja" value={k.resultado_caja} tone="emerald" testId="kpi-res-caja" />
        <KPICard icon={TrendingUp} label="Resultado devengado" value={k.resultado_devengado} tone="emerald" testId="kpi-res-deveng" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50">
          <h3 className="text-sm font-semibold text-slate-700">Serie últimos 12 meses</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Mes</th>
                <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Devengado</th>
                <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Cobrado</th>
                <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Pendiente</th>
                <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Egresos GC</th>
                <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Result. caja</th>
                <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Result. devengado</th>
              </tr>
            </thead>
            <tbody>
              {data.serie_12_meses.map((r) => (
                <tr key={r.periodo} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`gc-row-${r.periodo}`}>
                  <td className="py-2 px-3 font-medium text-slate-700">{monthLabel(r.periodo)}</td>
                  <td className="py-2 px-3 text-right text-slate-700">{fmt(r.devengado)}</td>
                  <td className="py-2 px-3 text-right text-emerald-700">{fmt(r.cobrado)}</td>
                  <td className="py-2 px-3 text-right text-amber-700">{fmt(r.pendiente)}</td>
                  <td className="py-2 px-3 text-right text-red-700">{fmt(r.egresos_gc)}</td>
                  <td className="py-2 px-3 text-right font-semibold text-slate-900">{fmt(r.resultado_caja)}</td>
                  <td className="py-2 px-3 text-right text-slate-700">{fmt(r.resultado_devengado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50"><h3 className="text-sm font-semibold text-slate-700">Por línea de negocio ({monthLabel(data.periodo)})</h3></div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr><th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Origen</th><th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Devengado</th><th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Cobrado</th><th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Pendiente</th></tr>
            </thead>
            <tbody>
              {Object.entries(data.por_origen).length === 0 && (
                <tr><td colSpan="4" className="py-4 text-center text-slate-400 text-xs">Sin movimientos para el mes.</td></tr>
              )}
              {Object.entries(data.por_origen).map(([k, v]) => (
                <tr key={k} className="border-b border-slate-100"><td className="py-1.5 px-3 text-slate-700">{ORIGEN_LABEL[k] || k}</td><td className="py-1.5 px-3 text-right text-slate-700">{fmt(v.devengado)}</td><td className="py-1.5 px-3 text-right text-emerald-700">{fmt(v.cobrado)}</td><td className="py-1.5 px-3 text-right text-amber-700">{fmt(v.pendiente)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50"><h3 className="text-sm font-semibold text-slate-700">Por edificio ({monthLabel(data.periodo)})</h3></div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr><th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Edificio</th><th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Devengado</th><th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Cobrado</th><th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Pendiente</th></tr>
            </thead>
            <tbody>
              {data.por_edificio.length === 0 && (
                <tr><td colSpan="4" className="py-4 text-center text-slate-400 text-xs">Sin movimientos para el mes.</td></tr>
              )}
              {data.por_edificio.map((v) => (
                <tr key={v.edificio_nombre} className="border-b border-slate-100"><td className="py-1.5 px-3 text-slate-700">{v.edificio_nombre}</td><td className="py-1.5 px-3 text-right text-slate-700">{fmt(v.devengado)}</td><td className="py-1.5 px-3 text-right text-emerald-700">{fmt(v.cobrado)}</td><td className="py-1.5 px-3 text-right text-amber-700">{fmt(v.pendiente)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Egresos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50"><h3 className="text-sm font-semibold text-slate-700">Egresos GC por categoría ({monthLabel(data.periodo)})</h3></div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50"><tr><th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Categoría</th><th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Cant.</th><th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Monto</th></tr></thead>
            <tbody>
              {(data.egresos_por_categoria || []).length === 0 && (
                <tr><td colSpan="3" className="py-4 text-center text-slate-400 text-xs">Sin egresos GC clasificados.</td></tr>
              )}
              {(data.egresos_por_categoria || []).map(c => (
                <tr key={c.categoria} className="border-b border-slate-100"><td className="py-1.5 px-3 text-slate-700 capitalize">{c.categoria}</td><td className="py-1.5 px-3 text-right text-slate-500">{c.cantidad}</td><td className="py-1.5 px-3 text-right text-red-700">{fmt(c.monto)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50"><h3 className="text-sm font-semibold text-slate-700">Movimientos del mes por clasificación</h3></div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50"><tr><th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Clasificación</th><th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Total</th></tr></thead>
            <tbody>
              {Object.entries(data.egresos_por_clasificacion || {}).map(([k, v]) => (
                <tr key={k} className="border-b border-slate-100">
                  <td className="py-1.5 px-3 text-slate-700 capitalize">{k.replaceAll('_', ' ')}{k === 'gasto_gc' ? ' (afecta resultado)' : ''}</td>
                  <td className={`py-1.5 px-3 text-right ${k === 'gasto_gc' ? 'text-red-700 font-semibold' : 'text-slate-500'}`}>{fmt(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============================= CxC =============================
const CxCView = ({ periodo, edificios, active }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [filterEdificio, setFilterEdificio] = useState('all');
  const [filterEstado, setFilterEstado] = useState('all');
  const [busqueda, setBusqueda] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [cobroDialog, setCobroDialog] = useState(null);
  const [anularDialog, setAnularDialog] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { periodo };
      if (filterEdificio !== 'all') params.edificio_id = filterEdificio;
      if (filterEstado !== 'all') params.estado = filterEstado;
      if (busqueda) params.busqueda = busqueda;
      const r = await gcApi.listCxC(params);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error('Error al cargar CxC');
    } finally { setLoading(false); }
  }, [periodo, filterEdificio, filterEstado, busqueda]);

  useEffect(() => { if (active) load(); }, [active, load]);

  const handleSyncRunner = async (kind) => {
    setSyncing(true);
    try {
      let r;
      if (kind === 'foresta') {
        r = await gcApi.syncFeesForesta(periodo);
        const d = r.data;
        const extras = d.errores?.length ? ` · ${d.errores.length} errores` : '';
        toast.success(`Foresta admin: ${d.cxc_creadas} nuevas, ${d.cxc_actualizadas} actualizadas, ${d.omitidas} omitidas${extras}`);
        if (d.errores?.length) d.errores.slice(0, 3).forEach(e => toast.warning(e));
      } else if (kind === 'te') {
        r = await gcApi.syncFeesTE(periodo);
        const d = r.data;
        toast.success(`TE admin: ${d.cxc_creadas} nuevas, ${d.cxc_actualizadas} actualizadas, ${d.cobros_compensacion} cobros por compensación`);
        if (d.errores?.length) d.errores.slice(0, 3).forEach(e => toast.warning(e));
      } else {
        r = await gcApi.syncComisiones();
        const d = r.data;
        toast.success(`Búsqueda: ${d.cxc_creadas} nuevas, ${d.cxc_actualizadas} actualizadas, ${d.cobros_mirror_creados} cobros mirror`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error sincronizando');
    } finally { setSyncing(false); }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const [rF, rTE, rC] = await Promise.allSettled([
        gcApi.syncFeesForesta(periodo),
        gcApi.syncFeesTE(periodo),
        gcApi.syncComisiones(),
      ]);
      const get = (r, k, d = 0) => r.status === 'fulfilled' ? (r.value.data?.[k] ?? d) : 0;
      const created = get(rF, 'cxc_creadas') + get(rTE, 'cxc_creadas') + get(rC, 'cxc_creadas');
      const updated = get(rF, 'cxc_actualizadas') + get(rTE, 'cxc_actualizadas') + get(rC, 'cxc_actualizadas');
      const cobrosComp = get(rTE, 'cobros_compensacion');
      const mirror = get(rC, 'cobros_mirror_creados');
      toast.success(`Sync ingresos GC: ${created} nuevas, ${updated} actualizadas, ${cobrosComp + mirror} cobros auto.`);
      await load();
    } catch (e) {
      toast.error('Error en sincronización global');
    } finally { setSyncing(false); }
  };

  const totales = useMemo(() => {
    const t = { devengado: 0, cobrado: 0, saldo: 0, count: items.length };
    items.forEach(it => { t.devengado += it.monto || 0; t.cobrado += it.monto_cobrado || 0; t.saldo += it.saldo || 0; });
    return t;
  }, [items]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Buscar concepto, cliente, unidad…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="pl-10 h-9" data-testid="gc-cxc-search" />
        </div>
        <Select value={filterEdificio} onValueChange={setFilterEdificio}>
          <SelectTrigger className="h-9 w-44" data-testid="gc-cxc-edif"><SelectValue placeholder="Edificio" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los edificios</SelectItem>
            {edificios.map(e => <SelectItem key={e.edificio_id} value={e.edificio_id}>{e.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterEstado} onValueChange={setFilterEstado}>
          <SelectTrigger className="h-9 w-36" data-testid="gc-cxc-estado"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="abiertos">Abiertos</SelectItem>
            <SelectItem value="pendiente">Pendiente</SelectItem>
            <SelectItem value="parcial">Parcial</SelectItem>
            <SelectItem value="cobrado">Cobrado</SelectItem>
            <SelectItem value="vencido">Vencido</SelectItem>
            <SelectItem value="anulado">Anulado</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => handleSyncRunner('busqueda')} disabled={syncing} className="h-9 hidden" data-testid="gc-cxc-sync-legacy">
          <RefreshCw className="w-4 h-4 mr-1.5" />
          Sync comisiones (legacy)
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="h-9" disabled={syncing} data-testid="gc-cxc-sync">
              {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Sincronizar ingresos GC
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel className="text-xs">Generar CxC automáticas del período</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSyncAll} data-testid="gc-sync-all">
              <RefreshCw className="w-3.5 h-3.5 mr-2" /> Sincronizar todos los ingresos
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleSyncRunner('foresta')} data-testid="gc-sync-foresta">
              Fee administración Foresta
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSyncRunner('te')} data-testid="gc-sync-te">
              Fee administración Torre Emilia
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSyncRunner('busqueda')} data-testid="gc-sync-busqueda">
              Fee búsqueda Foresta
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="ml-auto flex items-center gap-3">
          <div className="text-xs text-slate-500">
            <span className="mr-3">Total: <strong className="text-slate-800">{totales.count}</strong></span>
            <span className="mr-3">Devengado: <strong className="text-slate-800">{fmt(totales.devengado)}</strong></span>
            <span className="mr-3">Cobrado: <strong className="text-emerald-700">{fmt(totales.cobrado)}</strong></span>
            <span>Saldo: <strong className="text-amber-700">{fmt(totales.saldo)}</strong></span>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="h-9 bg-emerald-600 hover:bg-emerald-700" data-testid="gc-cxc-new"><Plus className="w-4 h-4 mr-1" /> Nueva CxC</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500">No hay cuentas por cobrar para el filtro actual.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Período</th>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Edificio / Unidad</th>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Cliente</th>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Concepto</th>
                <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Monto</th>
                <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Cobrado</th>
                <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Saldo</th>
                <th className="text-center text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Vto.</th>
                <th className="text-center text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Estado</th>
                <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.cxc_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`gc-cxc-row-${it.cxc_id}`}>
                  <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{monthLabel(it.periodo)}</td>
                  <td className="py-2 px-3 text-slate-700">
                    <div className="font-medium">{it.edificio_nombre || '—'}</div>
                    <div className="text-[11px] text-slate-500">{it.unidad_nombre || ''}</div>
                  </td>
                  <td className="py-2 px-3 max-w-[160px] truncate text-slate-700" title={it.cliente_nombre}>{it.cliente_nombre || '—'}</td>
                  <td className="py-2 px-3 max-w-[220px]">
                    <div className="truncate text-slate-800" title={it.concepto}>{it.concepto}</div>
                    <div className="text-[10px] text-slate-400">{ORIGEN_LABEL[it.origen_tipo] || it.origen_tipo}</div>
                  </td>
                  <td className="py-2 px-2 text-right text-slate-700 whitespace-nowrap">{fmt(it.monto)}</td>
                  <td className="py-2 px-2 text-right text-emerald-700 whitespace-nowrap">{fmt(it.monto_cobrado)}</td>
                  <td className="py-2 px-2 text-right font-semibold text-slate-900 whitespace-nowrap">{fmt(it.saldo)}</td>
                  <td className="py-2 px-2 text-center text-[12px] text-slate-600">{it.fecha_vencimiento ? formatDate(it.fecha_vencimiento) : '—'}</td>
                  <td className="py-2 px-2 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${ESTADO_TONE[it.estado] || ESTADO_TONE.pendiente}`}>{it.estado}</span>
                  </td>
                  <td className="py-2 px-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {it.estado !== 'cobrado' && it.estado !== 'anulado' && (
                        <Button size="sm" className="h-7 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700" onClick={() => setCobroDialog(it)} data-testid={`gc-cxc-cobrar-${it.cxc_id}`}>
                          <Wallet className="w-3 h-3 mr-1" /> Cobrar
                        </Button>
                      )}
                      {it.estado !== 'anulado' && it.monto_cobrado === 0 && (
                        <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" title="Anular" onClick={() => setAnularDialog(it)} data-testid={`gc-cxc-anular-${it.cxc_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NuevaCxCDialog
        open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load}
        edificios={edificios} periodoDefault={periodo}
      />
      <RegistrarCobroDialog cxc={cobroDialog} onClose={() => setCobroDialog(null)} onSaved={load} />
      <AnularCxCDialog cxc={anularDialog} onClose={() => setAnularDialog(null)} onSaved={load} />
    </div>
  );
};

const NuevaCxCDialog = ({ open, onClose, onCreated, edificios, periodoDefault }) => {
  const [form, setForm] = useState({
    periodo: periodoDefault, edificio_id: '', concepto: '', monto: '', moneda: 'ARS',
    fecha_vencimiento: '', cliente_nombre: '', observaciones: '', origen_tipo: 'manual',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(f => ({ ...f, periodo: periodoDefault })); }, [open, periodoDefault]);

  const handleSubmit = async () => {
    if (!form.concepto || !form.monto) { toast.error('Concepto y monto son obligatorios'); return; }
    setSaving(true);
    try {
      await gcApi.createCxC({
        ...form,
        monto: Number(form.monto),
        edificio_id: form.edificio_id || null,
        fecha_vencimiento: form.fecha_vencimiento || null,
      });
      toast.success('CxC creada');
      onClose(); onCreated();
      setForm({ periodo: periodoDefault, edificio_id: '', concepto: '', monto: '', moneda: 'ARS', fecha_vencimiento: '', cliente_nombre: '', observaciones: '', origen_tipo: 'manual' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al crear');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nueva cuenta por cobrar GC</DialogTitle>
          <DialogDescription>Se creará como `origen_tipo=manual` y entrará al tablero del mes seleccionado.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Período (YYYY-MM)</Label><Input value={form.periodo} onChange={(e) => setForm({ ...form, periodo: e.target.value })} data-testid="gc-new-periodo" /></div>
            <div>
              <Label className="text-xs">Edificio (opcional)</Label>
              <Select value={form.edificio_id || 'none'} onValueChange={(v) => setForm({ ...form, edificio_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="gc-new-edif"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Sin edificio —</SelectItem>
                  {edificios.map(e => <SelectItem key={e.edificio_id} value={e.edificio_id}>{e.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs">Cliente / Propietario (texto libre)</Label><Input value={form.cliente_nombre} onChange={(e) => setForm({ ...form, cliente_nombre: e.target.value })} data-testid="gc-new-cliente" /></div>
          <div><Label className="text-xs">Concepto *</Label><Input value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} placeholder="Ej. Fee admin julio, Honorario gestión, etc." data-testid="gc-new-concepto" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Monto *</Label><Input type="number" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} data-testid="gc-new-monto" /></div>
            <div>
              <Label className="text-xs">Moneda</Label>
              <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ARS">ARS</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Vencimiento</Label><Input type="date" value={form.fecha_vencimiento} onChange={(e) => setForm({ ...form, fecha_vencimiento: e.target.value })} data-testid="gc-new-vto" /></div>
          </div>
          <div><Label className="text-xs">Observaciones</Label><Input value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="gc-new-submit">{saving ? 'Guardando…' : 'Crear CxC'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RegistrarCobroDialog = ({ cxc, onClose, onSaved }) => {
  const [form, setForm] = useState({ fecha_cobro: '', monto_cobrado: '', medio_pago: '', observaciones: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (cxc) setForm({ fecha_cobro: new Date().toISOString().slice(0, 10), monto_cobrado: cxc.saldo, medio_pago: 'transferencia', observaciones: '' });
  }, [cxc]);

  const handleSubmit = async () => {
    const monto = Number(form.monto_cobrado);
    if (!monto || monto <= 0) { toast.error('Monto inválido'); return; }
    setSaving(true);
    try {
      await gcApi.registrarCobro(cxc.cxc_id, { ...form, monto_cobrado: monto, moneda: cxc.moneda });
      toast.success('Cobro registrado');
      onClose(); onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al registrar cobro');
    } finally { setSaving(false); }
  };

  if (!cxc) return null;
  return (
    <Dialog open={!!cxc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Registrar cobro</DialogTitle>
          <DialogDescription>
            <strong>{cxc.concepto}</strong> — Saldo actual: <strong>{fmt(cxc.saldo)}</strong>. Podés cobrar total o parcial.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Fecha cobro *</Label><Input type="date" value={form.fecha_cobro} onChange={(e) => setForm({ ...form, fecha_cobro: e.target.value })} data-testid="gc-cobro-fecha" /></div>
            <div><Label className="text-xs">Monto cobrado *</Label><Input type="number" value={form.monto_cobrado} onChange={(e) => setForm({ ...form, monto_cobrado: e.target.value })} data-testid="gc-cobro-monto" /></div>
          </div>
          <div><Label className="text-xs">Medio de pago</Label><Input value={form.medio_pago} onChange={(e) => setForm({ ...form, medio_pago: e.target.value })} placeholder="transferencia, efectivo, mp…" /></div>
          <div><Label className="text-xs">Observaciones</Label><Input value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="gc-cobro-submit">{saving ? 'Registrando…' : 'Registrar cobro'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AnularCxCDialog = ({ cxc, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  if (!cxc) return null;
  const handleConfirm = async () => {
    setSaving(true);
    try {
      await gcApi.anularCxC(cxc.cxc_id);
      toast.success('CxC anulada');
      onClose(); onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al anular');
    } finally { setSaving(false); }
  };
  return (
    <AlertDialog open={!!cxc} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Anular cuenta por cobrar</AlertDialogTitle>
          <AlertDialogDescription>
            Vas a anular <strong>{cxc.concepto}</strong> ({fmt(cxc.monto)}). Solo se puede anular si no tiene cobros aplicados.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={saving}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="gc-anular-confirm">Anular</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// ============================= COBROS =============================
const CobrosView = ({ periodo, edificios, active }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [filterEdificio, setFilterEdificio] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { periodo };
      if (filterEdificio !== 'all') params.edificio_id = filterEdificio;
      const r = await gcApi.listCobros(params);
      setItems(r.data?.items || []);
    } catch (e) { toast.error('Error al cargar cobros'); }
    finally { setLoading(false); }
  }, [periodo, filterEdificio]);

  useEffect(() => { if (active) load(); }, [active, load]);

  const total = useMemo(() => items.reduce((s, i) => s + (i.monto_cobrado || 0), 0), [items]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Select value={filterEdificio} onValueChange={setFilterEdificio}>
          <SelectTrigger className="h-9 w-44"><SelectValue placeholder="Edificio" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los edificios</SelectItem>
            {edificios.map(e => <SelectItem key={e.edificio_id} value={e.edificio_id}>{e.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-slate-500">
          <span className="mr-3">Total cobros: <strong className="text-slate-800">{items.length}</strong></span>
          <span>Importe: <strong className="text-emerald-700">{fmt(total)}</strong></span>
        </div>
      </div>
      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500">No hay cobros registrados en el período.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Fecha</th>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Cliente</th>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Edificio / Unidad</th>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-3">Concepto</th>
                <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Monto</th>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Medio</th>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2.5 px-2">Observ.</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.cobro_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`gc-cobro-row-${it.cobro_id}`}>
                  <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{formatDate(it.fecha_cobro)}</td>
                  <td className="py-2 px-3 text-slate-700 max-w-[180px] truncate" title={it.cliente_nombre}>{it.cliente_nombre || '—'}</td>
                  <td className="py-2 px-3 text-slate-700"><div>{it.edificio_nombre || '—'}</div><div className="text-[11px] text-slate-500">{it.unidad_nombre || ''}</div></td>
                  <td className="py-2 px-3 text-slate-700 max-w-[220px] truncate" title={it.concepto}>{it.concepto}</td>
                  <td className="py-2 px-2 text-right font-semibold text-emerald-700 whitespace-nowrap">{fmt(it.monto_cobrado)}</td>
                  <td className="py-2 px-2 text-slate-600 text-[12px]">{it.medio_pago || '—'}</td>
                  <td className="py-2 px-2 text-slate-500 text-[12px] max-w-[200px] truncate" title={it.observaciones}>{it.observaciones || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AnalistaGlobalCitizen;

// ============================= EGRESOS =============================
const CLASIF_LABEL = {
  gasto_gc: 'Gasto GC',
  gasto_edificio: 'Gasto edificio',
  gasto_propietario: 'Gasto propietario',
  recuperable: 'Recuperable',
  ignorar: 'Ignorar',
};
const CLASIF_TONE = {
  gasto_gc: 'bg-red-100 text-red-700 ring-1 ring-red-200',
  gasto_edificio: 'bg-slate-100 text-slate-600',
  gasto_propietario: 'bg-amber-50 text-amber-700',
  recuperable: 'bg-blue-50 text-blue-700',
  ignorar: 'bg-slate-50 text-slate-400',
};
const CATEGORIA_OPTS = ['sueldos','software','contable','marketing','bancos','impuestos','oficina','movilidad','mantenimiento','otros'];

function EgresosView({ periodo, edificios, active }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], total: 0, total_importe: 0, egresos_gc: 0, sin_clasificar: 0 });
  const [filterEdif, setFilterEdif] = useState('all');
  const [filterClasif, setFilterClasif] = useState('all');
  const [filterCat, setFilterCat] = useState('all');
  const [soloSinClasif, setSoloSinClasif] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [savingId, setSavingId] = useState(null);
  const [masivoOpen, setMasivoOpen] = useState(false);
  const [masivoForm, setMasivoForm] = useState({ clasificacion_gc: '', categoria_gc: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { periodo };
      if (filterEdif !== 'all') params.edificio_id = filterEdif;
      if (filterClasif !== 'all') params.clasificacion = filterClasif;
      if (filterCat !== 'all') params.categoria = filterCat;
      if (soloSinClasif) params.sin_clasificar = true;
      if (busqueda) params.busqueda = busqueda;
      const r = await gcApi.listEgresos(params);
      setData(r.data);
      setSelected(new Set());
    } catch (e) { toast.error('Error al cargar egresos'); }
    finally { setLoading(false); }
  }, [periodo, filterEdif, filterClasif, filterCat, soloSinClasif, busqueda]);

  useEffect(() => { if (active) load(); }, [active, load]);

  const handleInlineUpdate = async (movId, patch) => {
    setSavingId(movId);
    try {
      await gcApi.clasificarMovimiento(movId, patch);
      // Optimistic
      setData(prev => ({
        ...prev,
        items: prev.items.map(it => it.movimiento_id === movId ? { ...it, ...patch, clasificado: true } : it),
      }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al clasificar'); load(); }
    finally { setSavingId(null); }
  };

  const handleMasivo = async () => {
    if (selected.size === 0) { toast.error('Seleccioná al menos una fila'); return; }
    if (!masivoForm.clasificacion_gc && !masivoForm.categoria_gc) { toast.error('Definí clasificación o categoría'); return; }
    try {
      const payload = { movimiento_ids: Array.from(selected) };
      if (masivoForm.clasificacion_gc) payload.clasificacion_gc = masivoForm.clasificacion_gc;
      if (masivoForm.categoria_gc) payload.categoria_gc = masivoForm.categoria_gc;
      const r = await gcApi.clasificarMasivo(payload);
      toast.success(`Clasificación masiva: ${r.data.procesados} procesados, ${r.data.no_encontrados} no encontrados`);
      setMasivoOpen(false);
      setMasivoForm({ clasificacion_gc: '', categoria_gc: '' });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error en masiva'); }
  };

  const aplicarSugerencia = async (it) => {
    if (!it.sugerencia) return;
    await handleInlineUpdate(it.movimiento_id, { clasificacion_gc: it.sugerencia.clasificacion, categoria_gc: it.sugerencia.categoria });
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const ns = new Set(prev);
      if (ns.has(id)) ns.delete(id); else ns.add(id);
      return ns;
    });
  };
  const toggleAll = (checked) => {
    setSelected(checked ? new Set(data.items.map(it => it.movimiento_id)) : new Set());
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Buscar detalle, cuenta…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="pl-10 h-9" data-testid="gc-egr-search" />
        </div>
        <Select value={filterEdif} onValueChange={setFilterEdif}>
          <SelectTrigger className="h-9 w-44"><SelectValue placeholder="Edificio" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los edificios</SelectItem>
            {edificios.map(e => <SelectItem key={e.edificio_id} value={e.edificio_id}>{e.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterClasif} onValueChange={setFilterClasif}>
          <SelectTrigger className="h-9 w-44"><SelectValue placeholder="Clasificación" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas clasificaciones</SelectItem>
            {Object.entries(CLASIF_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="h-9 w-36"><SelectValue placeholder="Categoría" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {CATEGORIA_OPTS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant={soloSinClasif ? 'default' : 'outline'} className="h-9" onClick={() => setSoloSinClasif(!soloSinClasif)} data-testid="gc-egr-sin">Solo sin clasificar</Button>
        <Button variant="outline" disabled={selected.size === 0} onClick={() => setMasivoOpen(true)} className="h-9" data-testid="gc-egr-masiva-btn">
          Acción masiva ({selected.size})
        </Button>
        <div className="ml-auto text-xs text-slate-500">
          <span className="mr-3">Total: <strong>{data.total}</strong></span>
          <span className="mr-3">Importe: <strong>{fmt(data.total_importe)}</strong></span>
          <span className="mr-3">Egresos GC: <strong className="text-red-700">{fmt(data.egresos_gc)}</strong></span>
          <span>Sin clasif: <strong className="text-amber-700">{data.sin_clasificar}</strong></span>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : data.items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500">No hay movimientos del mayor para el período.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[1200px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-center py-2 px-2 w-8"><input type="checkbox" onChange={(e) => toggleAll(e.target.checked)} checked={selected.size > 0 && selected.size === data.items.length} /></th>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Fecha</th>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Detalle</th>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Cuenta</th>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Proveedor</th>
                <th className="text-right text-[11px] font-semibold uppercase text-slate-500 py-2 px-3">Monto</th>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2 px-3 min-w-[140px]">Clasificación</th>
                <th className="text-left text-[11px] font-semibold uppercase text-slate-500 py-2 px-3 min-w-[120px]">Categoría</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(it => {
                const isSel = selected.has(it.movimiento_id);
                return (
                  <tr key={it.movimiento_id} className={`border-b border-slate-100 hover:bg-slate-50 ${isSel ? 'bg-emerald-50/40' : ''}`} data-testid={`gc-egr-row-${it.movimiento_id}`}>
                    <td className="text-center py-1.5 px-2"><input type="checkbox" checked={isSel} onChange={() => toggleSelect(it.movimiento_id)} /></td>
                    <td className="py-1.5 px-3 text-[12px] text-slate-600 whitespace-nowrap">{it.fecha ? formatDate(it.fecha) : '—'}</td>
                    <td className="py-1.5 px-3 max-w-[260px]">
                      <div className="truncate text-slate-800" title={it.detalle}>{it.detalle || '—'}</div>
                      {it.sugerencia && !it.clasificado && (
                        <button className="text-[11px] text-emerald-700 hover:underline" onClick={() => aplicarSugerencia(it)} data-testid={`gc-egr-sug-${it.movimiento_id}`}>
                          Sugerencia: {CLASIF_LABEL[it.sugerencia.clasificacion]} / {it.sugerencia.categoria}
                        </button>
                      )}
                    </td>
                    <td className="py-1.5 px-3 max-w-[180px] truncate text-slate-600 text-[12px]" title={it.cuenta}>{it.cuenta || '—'}</td>
                    <td className="py-1.5 px-3 max-w-[160px] truncate text-slate-600 text-[12px]" title={it.subcuenta}>{it.subcuenta || '—'}</td>
                    <td className="py-1.5 px-3 text-right font-semibold text-slate-900 whitespace-nowrap">{fmt(it.importe)}</td>
                    <td className="py-1.5 px-2">
                      <Select value={it.clasificacion_gc || 'unset'} onValueChange={(v) => handleInlineUpdate(it.movimiento_id, { clasificacion_gc: v })}>
                        <SelectTrigger className="h-7 text-[11px]" data-testid={`gc-egr-clasif-${it.movimiento_id}`}>
                          <SelectValue placeholder="—">
                            {it.clasificacion_gc ? <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${CLASIF_TONE[it.clasificacion_gc] || ''}`}>{CLASIF_LABEL[it.clasificacion_gc]}</span> : <span className="text-slate-400">Sin clasificar</span>}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(CLASIF_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-1.5 px-2">
                      <Select value={it.categoria_gc || 'unset'} onValueChange={(v) => handleInlineUpdate(it.movimiento_id, { categoria_gc: v })}>
                        <SelectTrigger className="h-7 text-[11px]" data-testid={`gc-egr-cat-${it.movimiento_id}`}>
                          <SelectValue placeholder="—">{it.categoria_gc || <span className="text-slate-400">—</span>}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIA_OPTS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog acción masiva */}
      <Dialog open={masivoOpen} onOpenChange={(o) => !o && setMasivoOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clasificación masiva</DialogTitle>
            <DialogDescription>Aplicar a {selected.size} movimiento(s) seleccionado(s).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Clasificación</Label>
              <Select value={masivoForm.clasificacion_gc || ''} onValueChange={(v) => setMasivoForm(f => ({ ...f, clasificacion_gc: v }))}>
                <SelectTrigger data-testid="gc-egr-masiva-clasif"><SelectValue placeholder="No cambiar" /></SelectTrigger>
                <SelectContent>{Object.entries(CLASIF_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Categoría (solo aplica si clasif = Gasto GC)</Label>
              <Select value={masivoForm.categoria_gc || ''} onValueChange={(v) => setMasivoForm(f => ({ ...f, categoria_gc: v }))}>
                <SelectTrigger data-testid="gc-egr-masiva-cat"><SelectValue placeholder="No cambiar" /></SelectTrigger>
                <SelectContent>{CATEGORIA_OPTS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMasivoOpen(false)}>Cancelar</Button>
            <Button onClick={handleMasivo} className="bg-emerald-600 hover:bg-emerald-700" data-testid="gc-egr-masiva-submit">Aplicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

